import { useEffect } from "react";
import { useSearchParams } from "react-router";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { getSettings, updateSettings } from "@/lib/api/admin";
import { AlertCircle } from "lucide-react";
import { Spinner, SpinnerCenter } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CountryMultiSelect } from "@/components/CountryMultiSelect";
import {
  isSettingsTab,
  type SettingsTab,
  TAB_LIMITS_POINTS,
  TAB_PRICING_CATALOG,
  TAB_EVENTS,
  TAB_STAMPS_REFERRAL,
  TAB_WINDOWS_EXPIRY,
  TAB_APPOINTMENTS,
  TAB_MOBILE_APPS,
  TAB_STORE_VISIBILITY,
} from "@/lib/utils/settingsTabs";

// ─── Schema ───────────────────────────────────────────────────────────────────

const positiveInt = z
  .string()
  .refine((v) => !isNaN(parseInt(v, 10)) && parseInt(v, 10) > 0, "Must be a positive number.");

const nonNegativeInt = z
  .string()
  .refine(
    (v) => !isNaN(parseInt(v, 10)) && parseInt(v, 10) >= 0,
    "Must be zero or a positive number."
  );

const requiredStr = z.string().min(1, "Required.");

const settingsSchema = z
  .object({
    // Platform limits
    daily_discount_limit: positiveInt,
    daily_reward_limit: positiveInt,
    daily_stamp_redemption_limit: positiveInt,
    inviter_referral_points: positiveInt,
    invitee_referral_points: positiveInt,
    store_referral_inviter_bonus_points: positiveInt,
    store_referral_invitee_bonus_points: positiveInt,
    business_referral_bonus_points: positiveInt,
    signup_bonus_enabled: z.boolean(),
    signup_bonus_points: positiveInt,
    points_balance_cap: positiveInt,
    // Phone verification
    phone_verification_required: z.boolean(),
    phone_all_countries_allowed: z.boolean(),
    phone_allowed_countries: z.array(z.string()),
    // Pricing & item limits
    default_max_price: positiveInt,
    nightclub_max_price: positiveInt,
    bypass_max_price: positiveInt,
    min_restaurant_discount_percent: nonNegativeInt,
    max_menu_items_per_store: positiveInt,
    max_point_rewards_per_store: positiveInt,
    // Events
    default_event_guest_limit: positiveInt,
    max_guestlist_party_size: positiveInt,
    default_event_points: nonNegativeInt,
    event_checkin_grace_period_minutes: positiveInt,
    // Stamps & store referral
    default_stamp_daily_limit: positiveInt.refine(
      (v) => parseInt(v, 10) <= 100,
      "Must be 100 or less."
    ),
    min_store_referral_multiplier: positiveInt,
    max_store_referral_multiplier: positiveInt,
    // Expiry & windows
    points_expiry_months: positiveInt,
    stamp_expiry_months: positiveInt,
    referral_new_account_window_minutes: positiveInt,
    store_referral_purchase_window_days: positiveInt,
    // Appointments (platform defaults -- each is store-overridable via that store's own Cancellation Policy)
    default_appointment_cancellation_window_hours: positiveInt,
    default_appointment_no_show_grace_period_minutes: positiveInt,
    default_appointment_ban_threshold: positiveInt,
    default_appointment_on_time_cancellation_ban_threshold: positiveInt,
    default_appointment_strike_reset_days: positiveInt,
    default_appointment_ban_duration_days: positiveInt,
    // Customer app — iOS
    general_ios_current_version: requiredStr,
    general_ios_min_version: requiredStr,
    general_ios_store_url: requiredStr,
    // Customer app — Android
    general_android_current_version: requiredStr,
    general_android_min_version: requiredStr,
    general_android_store_url: requiredStr,
    // Customer app — update dialog
    general_update_dialog_title: requiredStr,
    general_update_dialog_description: requiredStr,
    general_update_dialog_cta: requiredStr,
    // DISCA Business app — iOS
    scanner_ios_current_version: requiredStr,
    scanner_ios_min_version: requiredStr,
    scanner_ios_store_url: requiredStr,
    // DISCA Business app — Android
    scanner_android_current_version: requiredStr,
    scanner_android_min_version: requiredStr,
    scanner_android_store_url: requiredStr,
    // DISCA Business app — update dialog
    scanner_update_dialog_title: requiredStr,
    scanner_update_dialog_description: requiredStr,
    scanner_update_dialog_cta: requiredStr,
    // Store visibility requirements
    visibility_require_schedule: z.boolean(),
    visibility_require_approval: z.boolean(),
    visibility_min_active_deals: nonNegativeInt,
    visibility_min_any_menu_items: nonNegativeInt,
    visibility_min_stamp_rewards: nonNegativeInt,
    visibility_min_point_rewards: nonNegativeInt,
  })
  .refine((v) => parseInt(v.nightclub_max_price, 10) >= parseInt(v.default_max_price, 10), {
    message: "Must be greater than or equal to the default max price.",
    path: ["nightclub_max_price"],
  })
  .refine((v) => parseInt(v.bypass_max_price, 10) >= parseInt(v.nightclub_max_price, 10), {
    message: "Must be greater than or equal to the nightclub max price.",
    path: ["bypass_max_price"],
  })
  .refine((v) => v.phone_all_countries_allowed || v.phone_allowed_countries.length > 0, {
    message: "Select at least one country, or allow all countries.",
    path: ["phone_allowed_countries"],
  });

type SettingsFormValues = z.infer<typeof settingsSchema>;

// ─── Field components ─────────────────────────────────────────────────────────

function Field({
  label,
  hint,
  error,
  inputMode = "text",
  id,
  name,
  type,
  onChange,
  ...inputProps
}: {
  label: string;
  hint?: string;
  error?: string;
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>["inputMode"];
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const fieldId = id ?? name;
  // "number" inputs are rendered as sanitized text so the browser spinner never shows.
  const isNumeric = type === "text" && (inputMode === "numeric" || inputMode === "decimal");
  return (
    <div className="space-y-1.5">
      <Label htmlFor={fieldId} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Input
        className="h-8 text-sm"
        id={fieldId}
        type={type}
        inputMode={inputMode}
        name={name}
        onChange={
          isNumeric
            ? (e) => {
                e.target.value =
                  inputMode === "decimal"
                    ? e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1")
                    : e.target.value.replace(/\D/g, "");
                onChange?.(e);
              }
            : onChange
        }
        {...inputProps}
      />
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function TextareaField({
  label,
  error,
  rows = 4,
  id,
  name,
  ...textareaProps
}: {
  label: string;
  error?: string;
  rows?: number;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const fieldId = id ?? name;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={fieldId} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Textarea
        className="text-sm resize-none"
        id={fieldId}
        rows={rows}
        name={name}
        {...textareaProps}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SettingsPage() {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab: SettingsTab =
    requestedTab && isSettingsTab(requestedTab) ? requestedTab : TAB_LIMITS_POINTS;

  function handleTabChange(value: string) {
    if (!isSettingsTab(value)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", value);
        return next;
      },
      { replace: true }
    );
  }

  const {
    data: settings,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["settings"],
    queryFn: () => getSettings(user!),
    enabled: !!user,
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors, isDirty },
  } = useForm<SettingsFormValues>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      daily_discount_limit: "",
      daily_reward_limit: "",
      daily_stamp_redemption_limit: "",
      inviter_referral_points: "",
      invitee_referral_points: "",
      store_referral_inviter_bonus_points: "",
      store_referral_invitee_bonus_points: "",
      business_referral_bonus_points: "",
      signup_bonus_enabled: true,
      signup_bonus_points: "",
      phone_verification_required: false,
      phone_all_countries_allowed: false,
      phone_allowed_countries: ["CA"],
      points_balance_cap: "",
      default_max_price: "",
      nightclub_max_price: "",
      bypass_max_price: "",
      min_restaurant_discount_percent: "",
      max_menu_items_per_store: "",
      max_point_rewards_per_store: "",
      default_event_guest_limit: "",
      max_guestlist_party_size: "",
      default_event_points: "",
      event_checkin_grace_period_minutes: "",
      default_stamp_daily_limit: "",
      min_store_referral_multiplier: "",
      max_store_referral_multiplier: "",
      points_expiry_months: "",
      stamp_expiry_months: "",
      referral_new_account_window_minutes: "",
      store_referral_purchase_window_days: "",
      default_appointment_cancellation_window_hours: "",
      default_appointment_no_show_grace_period_minutes: "",
      default_appointment_ban_threshold: "",
      default_appointment_on_time_cancellation_ban_threshold: "",
      default_appointment_strike_reset_days: "",
      default_appointment_ban_duration_days: "",
      general_ios_current_version: "",
      general_ios_min_version: "",
      general_ios_store_url: "",
      general_android_current_version: "",
      general_android_min_version: "",
      general_android_store_url: "",
      general_update_dialog_title: "",
      general_update_dialog_description: "",
      general_update_dialog_cta: "",
      scanner_ios_current_version: "",
      scanner_ios_min_version: "",
      scanner_ios_store_url: "",
      scanner_android_current_version: "",
      scanner_android_min_version: "",
      scanner_android_store_url: "",
      scanner_update_dialog_title: "",
      scanner_update_dialog_description: "",
      scanner_update_dialog_cta: "",
      visibility_require_schedule: true,
      visibility_require_approval: true,
      visibility_min_active_deals: "0",
      visibility_min_any_menu_items: "0",
      visibility_min_stamp_rewards: "1",
      visibility_min_point_rewards: "0",
    },
  });

  // Populate form once settings load
  useEffect(() => {
    if (settings) {
      reset({
        daily_discount_limit: String(settings.daily_discount_limit),
        daily_reward_limit: String(settings.daily_reward_limit),
        daily_stamp_redemption_limit: String(settings.daily_stamp_redemption_limit),
        inviter_referral_points: String(settings.inviter_referral_points),
        invitee_referral_points: String(settings.invitee_referral_points),
        store_referral_inviter_bonus_points: String(settings.store_referral_inviter_bonus_points),
        store_referral_invitee_bonus_points: String(settings.store_referral_invitee_bonus_points),
        business_referral_bonus_points: String(settings.business_referral_bonus_points),
        signup_bonus_enabled: settings.signup_bonus_enabled,
        signup_bonus_points: String(settings.signup_bonus_points),
        points_balance_cap: String(settings.points_balance_cap),
        phone_verification_required: settings.phone_verification_required ?? false,
        phone_all_countries_allowed: settings.phone_all_countries_allowed ?? false,
        phone_allowed_countries: settings.phone_allowed_countries ?? ["CA"],
        default_max_price: String(settings.default_max_price_cents / 100),
        nightclub_max_price: String(settings.nightclub_max_price_cents / 100),
        bypass_max_price: String(settings.bypass_max_price_cents / 100),
        min_restaurant_discount_percent: String(settings.min_restaurant_discount_percent),
        max_menu_items_per_store: String(settings.max_menu_items_per_store),
        max_point_rewards_per_store: String(settings.max_point_rewards_per_store),
        default_event_guest_limit: String(settings.default_event_guest_limit),
        max_guestlist_party_size: String(settings.max_guestlist_party_size),
        default_event_points: String(settings.default_event_points),
        event_checkin_grace_period_minutes: String(settings.event_checkin_grace_period_minutes),
        default_stamp_daily_limit: String(settings.default_stamp_daily_limit),
        min_store_referral_multiplier: String(settings.min_store_referral_multiplier),
        max_store_referral_multiplier: String(settings.max_store_referral_multiplier),
        points_expiry_months: String(settings.points_expiry_months),
        stamp_expiry_months: String(settings.stamp_expiry_months),
        referral_new_account_window_minutes: String(settings.referral_new_account_window_minutes),
        store_referral_purchase_window_days: String(settings.store_referral_purchase_window_days),
        default_appointment_cancellation_window_hours: String(
          settings.default_appointment_cancellation_window_hours
        ),
        default_appointment_no_show_grace_period_minutes: String(
          settings.default_appointment_no_show_grace_period_minutes
        ),
        default_appointment_ban_threshold: String(settings.default_appointment_ban_threshold),
        default_appointment_on_time_cancellation_ban_threshold: String(
          settings.default_appointment_on_time_cancellation_ban_threshold
        ),
        default_appointment_strike_reset_days: String(
          settings.default_appointment_strike_reset_days
        ),
        default_appointment_ban_duration_days: String(
          settings.default_appointment_ban_duration_days
        ),
        general_ios_current_version: settings.general_ios_current_version,
        general_ios_min_version: settings.general_ios_min_version,
        general_ios_store_url: settings.general_ios_store_url,
        general_android_current_version: settings.general_android_current_version,
        general_android_min_version: settings.general_android_min_version,
        general_android_store_url: settings.general_android_store_url,
        general_update_dialog_title: settings.general_update_dialog_title,
        general_update_dialog_description: settings.general_update_dialog_description,
        general_update_dialog_cta: settings.general_update_dialog_cta,
        scanner_ios_current_version: settings.scanner_ios_current_version,
        scanner_ios_min_version: settings.scanner_ios_min_version,
        scanner_ios_store_url: settings.scanner_ios_store_url,
        scanner_android_current_version: settings.scanner_android_current_version,
        scanner_android_min_version: settings.scanner_android_min_version,
        scanner_android_store_url: settings.scanner_android_store_url,
        scanner_update_dialog_title: settings.scanner_update_dialog_title,
        scanner_update_dialog_description: settings.scanner_update_dialog_description,
        scanner_update_dialog_cta: settings.scanner_update_dialog_cta,
        visibility_require_schedule: settings.visibility_requirements?.require_schedule ?? true,
        visibility_require_approval: settings.visibility_requirements?.require_approval ?? true,
        visibility_min_active_deals: String(
          settings.visibility_requirements?.min_active_deals ?? 0
        ),
        visibility_min_any_menu_items: String(
          settings.visibility_requirements?.min_any_menu_items ?? 0
        ),
        visibility_min_stamp_rewards: String(
          settings.visibility_requirements?.min_stamp_rewards ?? 1
        ),
        visibility_min_point_rewards: String(
          settings.visibility_requirements?.min_point_rewards ?? 0
        ),
      });
    }
  }, [settings, reset]);

  const mutation = useMutation({
    mutationFn: (values: SettingsFormValues) =>
      updateSettings(user!, {
        daily_discount_limit: parseInt(values.daily_discount_limit, 10),
        daily_reward_limit: parseInt(values.daily_reward_limit, 10),
        daily_stamp_redemption_limit: parseInt(values.daily_stamp_redemption_limit, 10),
        inviter_referral_points: parseInt(values.inviter_referral_points, 10),
        invitee_referral_points: parseInt(values.invitee_referral_points, 10),
        store_referral_inviter_bonus_points: parseInt(
          values.store_referral_inviter_bonus_points,
          10
        ),
        store_referral_invitee_bonus_points: parseInt(
          values.store_referral_invitee_bonus_points,
          10
        ),
        business_referral_bonus_points: parseInt(values.business_referral_bonus_points, 10),
        signup_bonus_enabled: values.signup_bonus_enabled,
        signup_bonus_points: parseInt(values.signup_bonus_points, 10),
        points_balance_cap: parseInt(values.points_balance_cap, 10),
        phone_verification_required: values.phone_verification_required,
        phone_all_countries_allowed: values.phone_all_countries_allowed,
        phone_allowed_countries: values.phone_allowed_countries,
        default_max_price_cents: parseInt(values.default_max_price, 10) * 100,
        nightclub_max_price_cents: parseInt(values.nightclub_max_price, 10) * 100,
        bypass_max_price_cents: parseInt(values.bypass_max_price, 10) * 100,
        min_restaurant_discount_percent: parseInt(values.min_restaurant_discount_percent, 10),
        max_menu_items_per_store: parseInt(values.max_menu_items_per_store, 10),
        max_point_rewards_per_store: parseInt(values.max_point_rewards_per_store, 10),
        default_event_guest_limit: parseInt(values.default_event_guest_limit, 10),
        max_guestlist_party_size: parseInt(values.max_guestlist_party_size, 10),
        default_event_points: parseInt(values.default_event_points, 10),
        event_checkin_grace_period_minutes: parseInt(values.event_checkin_grace_period_minutes, 10),
        default_stamp_daily_limit: parseInt(values.default_stamp_daily_limit, 10),
        min_store_referral_multiplier: parseInt(values.min_store_referral_multiplier, 10),
        max_store_referral_multiplier: parseInt(values.max_store_referral_multiplier, 10),
        points_expiry_months: parseInt(values.points_expiry_months, 10),
        stamp_expiry_months: parseInt(values.stamp_expiry_months, 10),
        referral_new_account_window_minutes: parseInt(
          values.referral_new_account_window_minutes,
          10
        ),
        store_referral_purchase_window_days: parseInt(
          values.store_referral_purchase_window_days,
          10
        ),
        default_appointment_cancellation_window_hours: parseInt(
          values.default_appointment_cancellation_window_hours,
          10
        ),
        default_appointment_no_show_grace_period_minutes: parseInt(
          values.default_appointment_no_show_grace_period_minutes,
          10
        ),
        default_appointment_ban_threshold: parseInt(values.default_appointment_ban_threshold, 10),
        default_appointment_on_time_cancellation_ban_threshold: parseInt(
          values.default_appointment_on_time_cancellation_ban_threshold,
          10
        ),
        default_appointment_strike_reset_days: parseInt(
          values.default_appointment_strike_reset_days,
          10
        ),
        default_appointment_ban_duration_days: parseInt(
          values.default_appointment_ban_duration_days,
          10
        ),
        general_ios_current_version: values.general_ios_current_version,
        general_ios_min_version: values.general_ios_min_version,
        general_ios_store_url: values.general_ios_store_url,
        general_android_current_version: values.general_android_current_version,
        general_android_min_version: values.general_android_min_version,
        general_android_store_url: values.general_android_store_url,
        general_update_dialog_title: values.general_update_dialog_title,
        general_update_dialog_description: values.general_update_dialog_description,
        general_update_dialog_cta: values.general_update_dialog_cta,
        scanner_ios_current_version: values.scanner_ios_current_version,
        scanner_ios_min_version: values.scanner_ios_min_version,
        scanner_ios_store_url: values.scanner_ios_store_url,
        scanner_android_current_version: values.scanner_android_current_version,
        scanner_android_min_version: values.scanner_android_min_version,
        scanner_android_store_url: values.scanner_android_store_url,
        scanner_update_dialog_title: values.scanner_update_dialog_title,
        scanner_update_dialog_description: values.scanner_update_dialog_description,
        scanner_update_dialog_cta: values.scanner_update_dialog_cta,
        visibility_requirements: {
          require_schedule: values.visibility_require_schedule,
          require_approval: values.visibility_require_approval,
          min_active_deals: parseInt(values.visibility_min_active_deals, 10),
          min_any_menu_items: parseInt(values.visibility_min_any_menu_items, 10),
          min_stamp_rewards: parseInt(values.visibility_min_stamp_rewards, 10),
          min_point_rewards: parseInt(values.visibility_min_point_rewards, 10),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Settings saved.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const signupBonusEnabled = watch("signup_bonus_enabled");
  const phoneAllCountriesAllowed = watch("phone_all_countries_allowed");

  if (isLoading) return <SpinnerCenter />;

  if (isError)
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
        <AlertCircle className="size-8 text-muted-foreground/50" />
        <p className="font-medium text-muted-foreground">Failed to load settings</p>
      </div>
    );

  return (
    <form onSubmit={handleSubmit((v) => mutation.mutate(v))} noValidate>
      <div className="max-w-3xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-sm text-muted-foreground">Platform configuration</p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="min-w-0">
            <TabsTrigger value={TAB_LIMITS_POINTS}>Limits & Points</TabsTrigger>
            <TabsTrigger value={TAB_PRICING_CATALOG}>Pricing & Catalog</TabsTrigger>
            <TabsTrigger value={TAB_EVENTS}>Events</TabsTrigger>
            <TabsTrigger value={TAB_STAMPS_REFERRAL}>Stamps & Referral</TabsTrigger>
            <TabsTrigger value={TAB_WINDOWS_EXPIRY}>Windows & Expiry</TabsTrigger>
            <TabsTrigger value={TAB_APPOINTMENTS}>Appointments</TabsTrigger>
            <TabsTrigger value={TAB_MOBILE_APPS}>Mobile Apps</TabsTrigger>
            <TabsTrigger value={TAB_STORE_VISIBILITY}>Store Visibility</TabsTrigger>
          </TabsList>

          <TabsContent value={TAB_LIMITS_POINTS} className="mt-4 space-y-6">
            {/* Daily limits */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Daily Limits</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Daily discount limit"
                  type="text"
                  inputMode="numeric"
                  error={errors.daily_discount_limit?.message}
                  {...register("daily_discount_limit")}
                />
                <Field
                  label="Daily reward limit"
                  type="text"
                  inputMode="numeric"
                  error={errors.daily_reward_limit?.message}
                  {...register("daily_reward_limit")}
                />
                <Field
                  label="Daily stamp redemption limit"
                  type="text"
                  inputMode="numeric"
                  error={errors.daily_stamp_redemption_limit?.message}
                  {...register("daily_stamp_redemption_limit")}
                />
              </CardContent>
            </Card>

            {/* Points */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Points</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Platform inviter bonus points"
                  type="text"
                  inputMode="numeric"
                  error={errors.inviter_referral_points?.message}
                  {...register("inviter_referral_points")}
                />
                <Field
                  label="Platform invitee bonus points"
                  type="text"
                  inputMode="numeric"
                  error={errors.invitee_referral_points?.message}
                  {...register("invitee_referral_points")}
                />
                <Field
                  label="Store inviter bonus points"
                  type="text"
                  inputMode="numeric"
                  error={errors.store_referral_inviter_bonus_points?.message}
                  {...register("store_referral_inviter_bonus_points")}
                />
                <Field
                  label="Store invitee bonus points"
                  type="text"
                  inputMode="numeric"
                  error={errors.store_referral_invitee_bonus_points?.message}
                  {...register("store_referral_invitee_bonus_points")}
                />
                <Field
                  label="Business referral bonus points"
                  hint="Extra bonus when a platform invitee's own business goes live."
                  type="text"
                  inputMode="numeric"
                  error={errors.business_referral_bonus_points?.message}
                  {...register("business_referral_bonus_points")}
                />
                <Field
                  label="Points balance cap"
                  hint="Lowering this won't reduce points users already have — it only limits future earning."
                  type="text"
                  inputMode="numeric"
                  error={errors.points_balance_cap?.message}
                  {...register("points_balance_cap")}
                />
                <div className="sm:col-span-2 space-y-3 rounded-md border p-3">
                  <div className="flex items-center gap-6">
                    <Label className="text-sm font-medium">Signup bonus</Label>
                    <Controller
                      control={control}
                      name="signup_bonus_enabled"
                      render={({ field }) => (
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      )}
                    />
                  </div>
                  <Field
                    label="Points"
                    type="text"
                    inputMode="numeric"
                    disabled={!signupBonusEnabled}
                    error={errors.signup_bonus_points?.message}
                    {...register("signup_bonus_points")}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Phone verification */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Phone Verification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  When enabled, app users must verify their phone before using the app, and new
                  users receive the signup bonus only after verifying. Enable only after the minimum
                  app version supports phone verification. Keep the Firebase SMS region policy in
                  sync with the allowed countries.
                </p>
                <div className="flex items-center gap-6">
                  <Label className="text-sm font-medium">Require phone verification</Label>
                  <Controller
                    control={control}
                    name="phone_verification_required"
                    render={({ field }) => (
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    )}
                  />
                </div>
                <div className="flex items-center gap-6">
                  <Label className="text-sm font-medium">Allow all countries</Label>
                  <Controller
                    control={control}
                    name="phone_all_countries_allowed"
                    render={({ field }) => (
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    )}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label
                    htmlFor="phone_allowed_countries"
                    className="text-xs text-muted-foreground"
                  >
                    Allowed countries
                  </Label>
                  <Controller
                    control={control}
                    name="phone_allowed_countries"
                    render={({ field }) => (
                      <CountryMultiSelect
                        id="phone_allowed_countries"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={phoneAllCountriesAllowed}
                        invalid={!!errors.phone_allowed_countries}
                      />
                    )}
                  />
                  {phoneAllCountriesAllowed ? (
                    <p className="text-xs text-muted-foreground">
                      All countries are allowed; this list is ignored.
                    </p>
                  ) : (
                    errors.phone_allowed_countries && (
                      <p className="text-xs text-destructive">
                        {errors.phone_allowed_countries.message}
                      </p>
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value={TAB_PRICING_CATALOG} className="mt-4 space-y-6">
            {/* Pricing & item limits */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Pricing & Item Limits</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Default max price ($)"
                  hint="Max price for menu items/rewards at a standard store."
                  type="text"
                  inputMode="numeric"
                  error={errors.default_max_price?.message}
                  {...register("default_max_price")}
                />
                <Field
                  label="Nightclub max price ($)"
                  hint="Must be >= the default max price."
                  type="text"
                  inputMode="numeric"
                  error={errors.nightclub_max_price?.message}
                  {...register("nightclub_max_price")}
                />
                <Field
                  label="Bypass max price ($)"
                  hint="Max price for stores with price limit bypass enabled. Must be >= the nightclub max price."
                  type="text"
                  inputMode="numeric"
                  error={errors.bypass_max_price?.message}
                  {...register("bypass_max_price")}
                />
                <Field
                  label="Min restaurant discount (%)"
                  type="text"
                  max="100"
                  inputMode="numeric"
                  error={errors.min_restaurant_discount_percent?.message}
                  {...register("min_restaurant_discount_percent")}
                />
                <Field
                  label="Max menu items per store"
                  type="text"
                  inputMode="numeric"
                  error={errors.max_menu_items_per_store?.message}
                  {...register("max_menu_items_per_store")}
                />
                <Field
                  label="Max point rewards per store"
                  type="text"
                  inputMode="numeric"
                  error={errors.max_point_rewards_per_store?.message}
                  {...register("max_point_rewards_per_store")}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value={TAB_EVENTS} className="mt-4 space-y-6">
            {/* Events */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Events</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Default guest limit"
                  hint="Applied when an event has no explicit guest limit."
                  type="text"
                  inputMode="numeric"
                  error={errors.default_event_guest_limit?.message}
                  {...register("default_event_guest_limit")}
                />
                <Field
                  label="Max guestlist party size"
                  hint="Max guest QR codes disca-glist's public form can request in one submission."
                  type="text"
                  inputMode="numeric"
                  error={errors.max_guestlist_party_size?.message}
                  {...register("max_guestlist_party_size")}
                />
                <Field
                  label="Default event points"
                  hint="Forced onto events created by non-admins (0–300)."
                  type="text"
                  max="300"
                  inputMode="numeric"
                  error={errors.default_event_points?.message}
                  {...register("default_event_points")}
                />
                <Field
                  label="Check-in grace period (minutes)"
                  hint="How long after an event ends its check-in QR still scans."
                  type="text"
                  inputMode="numeric"
                  error={errors.event_checkin_grace_period_minutes?.message}
                  {...register("event_checkin_grace_period_minutes")}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value={TAB_STAMPS_REFERRAL} className="mt-4 space-y-6">
            {/* Stamps */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Stamps</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Default daily stamp limit"
                  type="text"
                  inputMode="numeric"
                  hint="Stamps a customer can earn at one store per day, unless the store sets its own."
                  error={errors.default_stamp_daily_limit?.message}
                  {...register("default_stamp_daily_limit")}
                />
              </CardContent>
            </Card>

            {/* Store referral */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Store Referral</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Min store referral stamp multiplier"
                  type="text"
                  inputMode="numeric"
                  error={errors.min_store_referral_multiplier?.message}
                  {...register("min_store_referral_multiplier")}
                />
                <Field
                  label="Max store referral stamp multiplier"
                  type="text"
                  inputMode="numeric"
                  error={errors.max_store_referral_multiplier?.message}
                  {...register("max_store_referral_multiplier")}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value={TAB_WINDOWS_EXPIRY} className="mt-4 space-y-6">
            {/* Expiry & windows */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Expiry & Windows</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Points expiry (months)"
                  type="text"
                  inputMode="numeric"
                  error={errors.points_expiry_months?.message}
                  {...register("points_expiry_months")}
                />
                <Field
                  label="Stamp expiry (months)"
                  type="text"
                  inputMode="numeric"
                  error={errors.stamp_expiry_months?.message}
                  {...register("stamp_expiry_months")}
                />
                <Field
                  label="Referral new-account window (minutes)"
                  hint="How long after signup an account may still become a referral invitee."
                  type="text"
                  inputMode="numeric"
                  error={errors.referral_new_account_window_minutes?.message}
                  {...register("referral_new_account_window_minutes")}
                />
                <Field
                  label="Store referral purchase window (days)"
                  hint="Purchase deadline after an invitee applies a store referral invite link."
                  type="text"
                  inputMode="numeric"
                  error={errors.store_referral_purchase_window_days?.message}
                  {...register("store_referral_purchase_window_days")}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value={TAB_APPOINTMENTS} className="mt-4 space-y-6">
            {/* Appointments */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Appointments</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <p className="text-xs text-muted-foreground sm:col-span-2">
                  Platform-wide defaults for the manual per-store booking ban system. Any store can
                  override these on its own Cancellation Policy card.
                </p>
                <Field
                  label="Cancellation window (hours)"
                  hint="How many hours before an appointment's start it counts as an on-time cancellation."
                  type="text"
                  inputMode="numeric"
                  error={errors.default_appointment_cancellation_window_hours?.message}
                  {...register("default_appointment_cancellation_window_hours")}
                />
                <Field
                  label="No-show grace period (minutes)"
                  hint="How long after an appointment ends, with no check-in, it's marked a no-show."
                  type="text"
                  inputMode="numeric"
                  error={errors.default_appointment_no_show_grace_period_minutes?.message}
                  {...register("default_appointment_no_show_grace_period_minutes")}
                />
                <Field
                  label="Ban threshold (strikes)"
                  hint="Combined no-shows + late cancellations within the reset window before a store ban is imposed."
                  type="text"
                  inputMode="numeric"
                  error={errors.default_appointment_ban_threshold?.message}
                  {...register("default_appointment_ban_threshold")}
                />
                <Field
                  label="On-time cancellation threshold"
                  hint="A customer's own on-time cancellations within the reset window before a store ban is imposed — independent from the strikes above."
                  type="text"
                  inputMode="numeric"
                  error={errors.default_appointment_on_time_cancellation_ban_threshold?.message}
                  {...register("default_appointment_on_time_cancellation_ban_threshold")}
                />
                <Field
                  label="Strike reset window (days)"
                  hint="Rolling window over which both strike counts age out."
                  type="text"
                  inputMode="numeric"
                  error={errors.default_appointment_strike_reset_days?.message}
                  {...register("default_appointment_strike_reset_days")}
                />
                <Field
                  label="Ban duration (days)"
                  hint="Length of the store ban once the threshold is crossed."
                  type="text"
                  inputMode="numeric"
                  error={errors.default_appointment_ban_duration_days?.message}
                  {...register("default_appointment_ban_duration_days")}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value={TAB_MOBILE_APPS} className="mt-4 space-y-6">
            {/* Customer app — iOS */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Customer App — iOS</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-3">
                <Field
                  label="Current version"
                  error={errors.general_ios_current_version?.message}
                  {...register("general_ios_current_version")}
                />
                <Field
                  label="Min version"
                  error={errors.general_ios_min_version?.message}
                  {...register("general_ios_min_version")}
                />
                <Field
                  label="Store URL"
                  error={errors.general_ios_store_url?.message}
                  {...register("general_ios_store_url")}
                />
              </CardContent>
            </Card>

            {/* Customer app — Android */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Customer App — Android</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-3">
                <Field
                  label="Current version"
                  error={errors.general_android_current_version?.message}
                  {...register("general_android_current_version")}
                />
                <Field
                  label="Min version"
                  error={errors.general_android_min_version?.message}
                  {...register("general_android_min_version")}
                />
                <Field
                  label="Store URL"
                  error={errors.general_android_store_url?.message}
                  {...register("general_android_store_url")}
                />
              </CardContent>
            </Card>

            {/* Customer app — update dialog */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Customer App — Update Dialog</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field
                    label="Title"
                    error={errors.general_update_dialog_title?.message}
                    {...register("general_update_dialog_title")}
                  />
                  <Field
                    label="CTA"
                    error={errors.general_update_dialog_cta?.message}
                    {...register("general_update_dialog_cta")}
                  />
                </div>
                <TextareaField
                  label="Description"
                  error={errors.general_update_dialog_description?.message}
                  {...register("general_update_dialog_description")}
                />
              </CardContent>
            </Card>

            {/* DISCA Business app — iOS */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">DISCA Business App — iOS</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-3">
                <Field
                  label="Current version"
                  error={errors.scanner_ios_current_version?.message}
                  {...register("scanner_ios_current_version")}
                />
                <Field
                  label="Min version"
                  error={errors.scanner_ios_min_version?.message}
                  {...register("scanner_ios_min_version")}
                />
                <Field
                  label="Store URL"
                  error={errors.scanner_ios_store_url?.message}
                  {...register("scanner_ios_store_url")}
                />
              </CardContent>
            </Card>

            {/* DISCA Business app — Android */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">DISCA Business App — Android</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-3">
                <Field
                  label="Current version"
                  error={errors.scanner_android_current_version?.message}
                  {...register("scanner_android_current_version")}
                />
                <Field
                  label="Min version"
                  error={errors.scanner_android_min_version?.message}
                  {...register("scanner_android_min_version")}
                />
                <Field
                  label="Store URL"
                  error={errors.scanner_android_store_url?.message}
                  {...register("scanner_android_store_url")}
                />
              </CardContent>
            </Card>

            {/* DISCA Business app — update dialog */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">DISCA Business App — Update Dialog</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field
                    label="Title"
                    error={errors.scanner_update_dialog_title?.message}
                    {...register("scanner_update_dialog_title")}
                  />
                  <Field
                    label="CTA"
                    error={errors.scanner_update_dialog_cta?.message}
                    {...register("scanner_update_dialog_cta")}
                  />
                </div>
                <TextareaField
                  label="Description"
                  error={errors.scanner_update_dialog_description?.message}
                  {...register("scanner_update_dialog_description")}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value={TAB_STORE_VISIBILITY} className="mt-4 space-y-6">
            {/* Store Visibility Requirements */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Store Visibility Requirements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  A store must meet all enabled requirements to appear in the public listing.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2.5">
                    <div>
                      <p className="text-sm font-medium">Require schedule</p>
                      <p className="text-xs text-muted-foreground">
                        Store must have operating hours set
                      </p>
                    </div>
                    <Controller
                      control={control}
                      name="visibility_require_schedule"
                      render={({ field }) => (
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      )}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2.5">
                    <div>
                      <p className="text-sm font-medium">Require admin approval</p>
                      <p className="text-xs text-muted-foreground">
                        Store must be approved by DISCA
                      </p>
                    </div>
                    <Controller
                      control={control}
                      name="visibility_require_approval"
                      render={({ field }) => (
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      )}
                    />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Min active deals"
                    type="text"
                    inputMode="numeric"
                    hint="Active Target, Combo and Multi-buy deals, counted together. 0 = not required."
                    error={errors.visibility_min_active_deals?.message}
                    {...register("visibility_min_active_deals")}
                  />
                  <Field
                    label="Min menu items"
                    type="text"
                    inputMode="numeric"
                    hint="Active menu items. 0 = not required."
                    error={errors.visibility_min_any_menu_items?.message}
                    {...register("visibility_min_any_menu_items")}
                  />
                  <Field
                    label="Min stamp rewards"
                    type="text"
                    inputMode="numeric"
                    hint="Active stamp rewards on an enabled stamp card"
                    error={errors.visibility_min_stamp_rewards?.message}
                    {...register("visibility_min_stamp_rewards")}
                  />
                  <Field
                    label="Min point rewards"
                    type="text"
                    inputMode="numeric"
                    hint="Active point rewards"
                    error={errors.visibility_min_point_rewards?.message}
                    {...register("visibility_min_point_rewards")}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* -bottom-6/-mx-4 bleed past AppShell's <main> padding so this sits flush with its true edge. */}
      <div className="sticky -bottom-6 mt-4 -mx-4 border-t bg-background pt-4 pb-4 md:pb-10 px-4 md:px-6">
        <div className="max-w-3xl">
          <Button type="submit" disabled={mutation.isPending || !isDirty}>
            {mutation.isPending ? (
              <Spinner className="size-4 border-primary-foreground" />
            ) : (
              "Save Settings"
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
